import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, Search } from 'lucide-react';

import MainUrl from '../../MainUrl';
import QuoteModal from './QuoteModal';

const CategoryButton = ({ name, isActive, onClick }) => {
  return (
    <button
      className={`px-5 py-3 rounded-full text-sm font-medium transition-colors ${isActive
        ? 'bg-green-100 text-green-700'
        : 'bg-green-700 text-white hover:bg-green-600'
        }`}
      onClick={onClick}
    >
      {name}
    </button>
  );
};

const PricingTable = ({ items }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedItems = React.useMemo(() => {
    if (!sortConfig.key) return items;

    return [...items].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? (
        <ChevronUp size={16} className="inline ml-1" />
      ) : (
        <ChevronDown size={16} className="inline ml-1" />
      );
    }
    return null;
  };

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="w-full bg-white">
        <thead>
          <tr className="bg-green-700 text-white">
            <th
              className="px-4 py-2 text-left text-sm font-semibold cursor-pointer hover:bg-green-600 border-b border-green-600"
              onClick={() => requestSort('title')}
            >
              Item{getSortIcon('title')}
            </th>
            <th
              className="px-4 py-2 text-left text-sm font-semibold cursor-pointer hover:bg-green-600 border-b border-green-600"
              onClick={() => requestSort('category')}
            >
              Category{getSortIcon('category')}
            </th>
            <th
              className="px-4 py-2 text-left text-sm font-semibold cursor-pointer hover:bg-green-600 border-b border-green-600"
              onClick={() => requestSort('service')}
            >
              Service Type{getSortIcon('service')}
            </th>
            <th
              className="px-4 py-2 text-right text-sm font-semibold cursor-pointer hover:bg-green-600 border-b border-green-600"
              onClick={() => requestSort('price')}
            >
              Price (₹){getSortIcon('price')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((item, index) => (
            <tr
              key={`${item.id}`}
              className={`border-b border-gray-200 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-green-50 transition-colors`}
            >
              <td className="px-4 py-2 text-sm text-gray-800">{item.title}</td>
              <td className="px-4 py-2 text-sm text-gray-600">
                <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                  {item.category}
                </span>
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">{item.service}</td>
              <td className="px-4 py-2 text-right text-sm font-semibold text-gray-800">
                {item.displayPrice}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Pricing = () => {
  // States for UI interaction
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // States for API data
  const [categories, setCategories] = useState([]);
  const [pricingItems, setPricingItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch categories and default pricing data on component mount
  useEffect(() => {
    fetchCategories();
    // Fetch default pricing with pincode as 0
    fetchPricingData("0");
  }, []);

  // Function to fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${MainUrl}web/getCategoryList`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        // Add "All Services" to the end of the categories array
        setCategories([
          ...data.data,
          { category_id: 0, category_name: 'All Services' }
        ]);
      } else {
        setError('Failed to fetch categories');
      }
    } catch (err) {
      setError('Error fetching categories: ' + err.message);
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch pricing data based on pincode
  const fetchPricingData = async (pincode) => {
    try {
      setLoading(true);
      const response = await fetch(`${MainUrl}web/getLocationWisePrice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pincode }),
      });

      const data = await response.json();

      if (data.success) {
        const SERVICE_LABELS = {
          STEAM_IRON: 'Steam Iron',
          LAUNDRY_KG: 'Laundry /KG',
          LAUNDRY_PC: 'Laundry /PC',
          DRY_CLEAN: 'Dry Clean',
        };
        // Transform the data into our desired pricing items format
        const formattedPricingItems = data.data.map((item) => {
          const p = item.PricingMaster;
          const svcLabel = SERVICE_LABELS[p.service_type] || p.service_type || '';
          const displayPrice = p.price_note || (p.price != null ? `₹${p.price}` : 'N/A');
          // Item is the actual garment (new hierarchy level); fall back to the
          // subcategory name if an item isn't attached for safety.
          const itemName = p.ItemMaster?.item_name || p.SubcategoryMaster.subcategory_name;
          return {
            id: p.price_id,
            title: itemName,
            group: p.SubcategoryMaster.subcategory_name,
            service: svcLabel,
            category: p.CategoryMaster.category_name,
            price: p.price,
            price_note: p.price_note,
            displayPrice,
            price_unit: p.CategoryMaster.price_unit,
          };
        });

        setPricingItems(formattedPricingItems);
      } else {
        setPricingItems([]);
        setError('Failed to fetch pricing data');
      }
    } catch (err) {
      setError('Error fetching pricing data: ' + err.message);
      console.error('Error fetching pricing data:', err);
      setPricingItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle category button click with toggle behavior
  const handleCategoryClick = (categoryName) => {
    if (selectedCategory === categoryName) {
      // If already selected, deselect it
      setSelectedCategory('');
    } else {
      // Otherwise select the new category
      setSelectedCategory(categoryName);
    }
  };

  // Filter pricing items based on category and search term
  const filteredItems = pricingItems.filter(item => {
    // Filter by search term (case insensitive) across item name, subcategory group, and category
    if (searchTerm && !item.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !item.group.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !item.category.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !item.service.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filter by category
    if (selectedCategory && selectedCategory !== 'All Services' && item.category !== selectedCategory) {
      return false;
    }

    return true;
  });

  // Group the filtered items by subcategory (garment group) so each selected
  // category can show its garment groups as sub-headings with items underneath.
  const groupedItems = React.useMemo(() => {
    const groups = [];
    const groupIndex = new Map();

    filteredItems.forEach((item) => {
      const key = item.group || 'Other';
      if (!groupIndex.has(key)) {
        groupIndex.set(key, groups.length);
        groups.push({ name: key, items: [] });
      }
      groups[groupIndex.get(key)].items.push(item);
    });

    return groups;
  }, [filteredItems]);

  return (
    <section id="pricing" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium mb-4">
            Our Pricing
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6">
            Transparent & Affordable Rates
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Browse our pricing list to find the best services for your needs. All prices are inclusive of taxes.
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-8 text-center">
            {error}
          </div>
        )}

        {/* Search Box */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Search services..."
            />
          </div>
        </div>

        {/* Category Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((category) => (
            <CategoryButton
              key={category.category_id}
              name={category.category_name}
              isActive={selectedCategory === category.category_name}
              onClick={() => handleCategoryClick(category.category_name)}
            />
          ))}
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        )}

        {/* Pricing Table, grouped by garment group (subcategory) within the selected category */}
        {!loading && (
          <div className="mb-12 space-y-10">
            {(selectedCategory || searchTerm) ? (
              groupedItems.length > 0 ? (
                groupedItems.map((group) => (
                  <div key={group.name}>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 border-l-4 border-green-700 pl-3">
                      {group.name}
                    </h3>
                    <PricingTable items={group.items} />
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No services found for the selected criteria.
                </div>
              )
            ) : (
              <div className="col-span-full text-center py-12 text-gray-500">
                Please select a category or search for services to view pricing.
              </div>
            )}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-4">
            Can't find what you're looking for? Schedule Free Pickup Now.
          </p>
          <button
            onClick={() => setShowQuoteModal(true)}
            className="inline-flex items-center px-6 py-3 bg-neutral-500 text-white rounded-full font-medium hover:bg-green-700 transition-colors"
          >
            Get a Quote <ArrowRight size={18} className="ml-2" />
          </button>
        </div>

        {/* Quote Modal */}
        <QuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          services={categories}
          selectedLocationId={"0"}
        />
      </div>
    </section>
  );
};

export default Pricing;